import React, { useState, useEffect } from 'react'
import Head from 'next/head'
import Home from '@/components/Home'
import { GetServerSideProps } from 'next'

import GetMostSoldProducts from '@/services/api/products/GetMostSoldProducts'
import { GetAgeGroupOptions } from '@/services/api/products/GetAttributeOption'
import GetFeaturedCategories from '@/services/api/products/GetFeaturedCategories'
import { GetHome } from '@/services/api/cms'
import GetAllCategories from '@/services/api/products/GetAllCategories'
import GetPersonalizedNotebooks from '@/services/api/products/GetPersonalizedNotebooks'

import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useUserLocaleChoice } from '@/hooks/useUserLocaleChoice'
import useUserPreferences from '@/hooks/useUserPreferences'

/**
 * SERVER SIDE RENDERING
 * Home CMS data will now ALWAYS be fresh
 */
export const getServerSideProps: GetServerSideProps = async ({ locale }) => {
  try {
    const defaultCountry = 'IL'
    const defaultCurrency = 'ILS'

    const [
      most_sold_products,
      personalized_notebooks,
      age_group_options,
      featured_categories,
      home,
      categories,
    ] = await Promise.all([
      GetMostSoldProducts(defaultCountry, defaultCurrency),
      GetPersonalizedNotebooks(defaultCountry, defaultCurrency),
      GetAgeGroupOptions(),
      GetFeaturedCategories(),
      GetHome(),
      GetAllCategories(),
    ])

    return {
      props: {
        ...(await serverSideTranslations(locale ?? 'en', [
          'products',
          'productsHeroBanner',
          'categoryTabs',
          'filterModal',
          'filterSidebar',
          'desktopHeader',
          'footer',
          'common',
          'account',
          'alerts',
          'address',
          'cart',
          'contactus',
          'deliveryPolicy',
          'notfound',
          'payment',
          'personalizeModal',
          'product',
          'removePhone',
        ])),
        most_sold_products,
        personalized_notebooks,
        age_group_options,
        featured_categories,
        home,
        categories,
      },
    }
  } catch (error) {
    console.error('Error fetching homepage data:', error)

    return {
      props: {
        ...(await serverSideTranslations(locale ?? 'en', [
          'desktopHeader',
          'footer',
        ])),
        most_sold_products: null,
        personalized_notebooks: null,
        age_group_options: null,
        featured_categories: null,
        home: null,
        categories: null,
      },
    }
  }
}

interface Props {
  most_sold_products: any
  personalized_notebooks: any
  age_group_options: any
  featured_categories: any
  home: any
  categories: any
}

export default function Homepage({
  most_sold_products,
  personalized_notebooks,
  age_group_options,
  featured_categories,
  home,
  categories,
}: Props) {
  const { userSavedChoice, isClient } = useUserLocaleChoice()
  const preferences = useUserPreferences()

  const [mostSoldProducts, setMostSoldProducts] = useState(most_sold_products)
  const [notebooks, setNotebooks] = useState(personalized_notebooks)
  const [isRefetching, setIsRefetching] = useState(false)

  /**
   * Refetch products ONLY if user currency/country differs
   */
  useEffect(() => {
    const fetchWithUserPreferences = async () => {
      if (!preferences?.currencyCode || !preferences?.countryCode) return

      const { currencyCode, countryCode } = preferences

      if (currencyCode !== 'ILS' || countryCode !== 'IL') {
        setIsRefetching(true)
        try {
          const updatedProducts = await GetMostSoldProducts(countryCode, currencyCode)
          const updatedNotebooks = await GetPersonalizedNotebooks(countryCode, currencyCode)

          setMostSoldProducts(updatedProducts)
          setNotebooks(updatedNotebooks)
        } catch (error) {
          console.error('Currency-based refetch failed:', error)
        } finally {
          setIsRefetching(false)
        }
      } else {
        setMostSoldProducts(most_sold_products)
        setNotebooks(personalized_notebooks)
      }
    }

    fetchWithUserPreferences()
  }, [
    preferences?.currencyCode,
    preferences?.countryCode,
    most_sold_products,
    personalized_notebooks,
  ])

  /**
   * Fallback UI
   */
  if (!home) {
    return (
      <main className="min-h-screen flex items-center justify-center">
        <h1 className="text-xl text-red-500">
          Homepage data is unavailable.
        </h1>
      </main>
    )
  }

  const currentLocale = isClient ? userSavedChoice.locale : 'ar'

  const title = currentLocale === 'ar' ? home.title?.ar : home.title?.en
  const description =
    currentLocale === 'ar'
      ? home.seo?.description?.ar
      : home.seo?.description?.en

  const author =
    currentLocale === 'ar'
      ? home.seo?.author?.ar
      : home.seo?.author?.en

  const metaTitle =
    currentLocale === 'ar'
      ? home.seo?.title?.ar
      : home.seo?.title?.en

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="author" content={author} />
        <meta name="title" content={metaTitle} />
      </Head>

      <Home
        most_sold_products={mostSoldProducts}
        personalized_notebooks={notebooks}
        age_group_options={age_group_options}
        featured_categories={featured_categories}
        home={home}
        categories={categories}
      />
    </>
  )
}
